import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import { useTranslation } from 'react-i18next';
import EditableNote, { hasText } from './EditableNote';

// The customer's and the vehicle's internal note (sisäinen muistiinpano). Internal to the
// workshop: render these only in the authenticated app, never on anything the customer sees.

function NoteSection({ heading, note, testId }) {
  return (
    <Box data-testid={testId}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {heading}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {note.trim()}
      </Typography>
    </Box>
  );
}

/**
 * Warning box with the customer's and/or the vehicle's internal note, each under its own heading.
 * Renders nothing when both are empty.
 */
export function InternalNotesAlert({ customerNote, vehicleNote, action, sx }) {
  const { t } = useTranslation();
  const showCustomer = hasText(customerNote);
  const showVehicle = hasText(vehicleNote);
  if (!showCustomer && !showVehicle) return null;

  return (
    <Alert
      severity="warning"
      /* A note icon, not the default warning triangle: the problem description uses that one */
      icon={<StickyNote2OutlinedIcon fontSize="inherit" />}
      data-testid="internal-notes-alert"
      action={action}
      sx={{ borderRadius: '12px', '& .MuiAlert-message': { width: '100%' }, ...sx }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {showCustomer && (
          <NoteSection heading={t('internalNote.customer')} note={customerNote} testId="internal-note-customer" />
        )}
        {showVehicle && (
          <NoteSection heading={t('internalNote.vehicle')} note={vehicleNote} testId="internal-note-vehicle" />
        )}
      </Box>
    </Alert>
  );
}

/**
 * The note of one customer or vehicle, edited in place with explicit Save/Cancel.
 * `kind` is 'customer' or 'vehicle'; `onSave(text)` persists the note and throws on failure.
 */
export function InternalNoteEditor({ kind, note, canEdit, onSave, sx }) {
  const { t } = useTranslation();
  return (
    <EditableNote
      value={note}
      canEdit={canEdit}
      onSave={onSave}
      sx={sx}
      testId={(part) => `internal-note-${part}-${kind}`}
      labels={{
        label: t('internalNote.label'),
        help: t('internalNote.help'),
        add: t('internalNote.add'),
        edit: t('internalNote.edit'),
        saveError: t('internalNote.saveError'),
      }}
      renderView={({ action, sx: viewSx }) => (
        <InternalNotesAlert
          customerNote={kind === 'customer' ? note : ''}
          vehicleNote={kind === 'vehicle' ? note : ''}
          sx={viewSx}
          action={action}
        />
      )}
    />
  );
}
